import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'config/axiosConfig';
import { Loader2 } from 'lucide-react';
import { apiRoutes } from "util/apiRoutes";

/**
 * Component that redirects from /comment/:id to the appropriate entity page with the comment focused
 */
const CommentRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCommentAndRedirect = async () => {
      if (!id) {
        setError('Comment ID is missing');
        return;
      }

      try {
        // Fetch the comment data
        const response = await api.get(apiRoutes.comments.byId(id));
        const comment = response.data;
        
        if (!comment) {
          setError('Comment not found');
          return;
        }

        // Get the target entity type and ID
        let targetType = comment.commentable_type.toLowerCase();
        const targetId = comment.commentable_id;
        
        // Special case for comment replies - need to get the root entity
        if (targetType === 'comment') {
          // For nested comments, fetch the parent comment to find the actual entity
          const parentResponse = await api.get(apiRoutes.comments.byId(targetId));
          const parentComment = parentResponse.data;
          
          if (parentComment) {
            targetType = parentComment.commentable_type.toLowerCase();
            const rootId = parentComment.commentable_id;
            
            // Handle redirect based on entity type
            await handleEntityRedirect(targetType, rootId, id);
          } else {
            setError('Could not find parent comment');
          }
        } else {
          // Direct comment on an entity
          await handleEntityRedirect(targetType, targetId, id);
        }
      } catch (err) {
        console.error('Error fetching comment:', err);
        setError('Error loading comment. Please try again.');
      }
    };

    // Helper function to handle entity-specific redirects
    const handleEntityRedirect = async (entityType: string, entityId: string | number, commentId: string) => {
      try {
        // Special handling for issues - need to get the canonical name
        if (entityType === 'issue') {
          try {
            const issueResponse = await api.get(apiRoutes.issues.byId(entityId));
            const issueData = issueResponse.data;
            
            // Redirect to the canonical issue URL with comment ID
            navigate(`/issue/${issueData.cname}/comments/${commentId}`);
          } catch (error) {
            // Fallback to direct ID if issue lookup fails
            navigate(`/issue/${entityId}/comments/${commentId}`);
          }
        } else if (entityType === 'team') {
          navigate(`/partea/${entityId}/comments/${commentId}`);
        } else {
          // Standard redirect for other entity types
          navigate(`/${entityType}/${entityId}/comments/${commentId}`);
        }
      } catch (err) {
        console.error(`Error in entity redirect for ${entityType}:`, err);
        // Fallback to direct navigation if specific handling fails
        navigate(`/${entityType}/${entityId}/comments/${commentId}`);
      }
    };

    if (id) {
      fetchCommentAndRedirect();
    } else {
      setError('Comment ID is missing');
    }
  }, [id, navigate]);

  if (error) {
    return (
      <div className="comment-redirect-error flex h-[80vh] flex-col items-center justify-center">
        <p className="text-lg font-semibold text-warning-red dark:text-dark-warning-red" role="alert">
          {error}
        </p>
        <p className="mt-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          The comment you're looking for couldn't be found.
        </p>
      </div>
    );
  }

  return (
    <div className="comment-redirect-loading flex h-[80vh] items-center justify-center">
      <Loader2 className="size-6 animate-spin text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-label="Redirecting" />
    </div>
  );
};

export default CommentRedirect; 